<template>
  <div class="deviceListWrapper dark-theme" style="background-color: #121212; min-height: 100vh; color: #ffffff">
    <div class="pa-4 mx-auto" style="max-width: 1200px">
      <div class="mb-8 mt-4 d-flex flex-column flex-md-row align-md-center justify-space-between">
        <v-text-field
          dark
          v-model="search"
          label="Gerät oder Übung suchen..."
          prepend-inner-icon="mdi-magnify"
          hide-details
          clearable
          outlined
          dense
          background-color="#1a1a1a"
          class="modern-input flex-grow-1 mr-md-4 mb-4 mb-md-0"
          style="max-width: 600px"></v-text-field>

        <v-btn color="#00E676" height="44" class="black--text font-weight-bold tracking-wide px-8" depressed @click="openDeviceDialog(null)">
          <v-icon left>mdi-plus</v-icon> NEUES GERÄT
        </v-btn>
      </div>

      <v-expansion-panels dark multiple v-model="activePanels" flat class="transparent-panels">
        <v-expansion-panel v-for="group in groupedCategories" :key="group.name" class="mb-4" style="background-color: transparent !important">
          <v-expansion-panel-header class="px-0 py-2">
            <div class="d-flex align-center w-100">
              <h2 class="text-h6 font-weight-bold white--text text-uppercase tracking-wide">{{ group.name }}</h2>
              <v-chip dark small color="#333" class="ml-3 font-weight-bold">{{ group.devices.length }}</v-chip>
            </div>
            <template v-slot:actions>
              <v-icon color="#00E676">mdi-chevron-down</v-icon>
            </template>
          </v-expansion-panel-header>

          <v-divider dark class="mb-4" style="border-color: #2a2a2a"></v-divider>

          <v-expansion-panel-content class="px-0">
            <v-row dense>
              <v-col cols="12" md="6" lg="4" v-for="device in group.devices" :key="device._id" class="pa-2">
                <v-card
                  dark
                  color="#1a1a1a"
                  class="device-card elevation-0 d-flex flex-row overflow-hidden h-100"
                  @click="openDeviceDialog(device)"
                  style="border: 1px solid #2a2a2a; border-radius: 8px; cursor: pointer">
                  <div class="image-wrapper d-flex align-center justify-center">
                    <v-img
                      :src="device.picture ? '/img/uebungen/' + device.picture : '/img/einmalig/imageUploadPlaceholder.jpg'"
                      height="100%"
                      width="100%"
                      cover
                      transition="fade-transition">
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-icon color="#333" size="30">mdi-dumbbell</v-icon>
                        </v-row>
                      </template>
                    </v-img>
                  </div>

                  <div class="d-flex flex-column flex-grow-1 pa-4 justify-space-between" style="width: calc(100% - 100px)">
                    <div>
                      <div class="d-flex align-center mb-1">
                        <v-chip v-if="device.shortForm" x-small color="#00E676" class="black--text font-weight-bold px-2 text-uppercase">
                          {{ device.shortForm }}
                        </v-chip>
                      </div>
                      <div class="text-subtitle-1 font-weight-bold white--text line-clamp-2" style="line-height: 1.2">
                        {{ device.title }}
                      </div>
                    </div>

                    <div class="d-flex justify-space-between align-end mt-3">
                      <div class="text-caption grey--text d-flex align-center font-weight-medium">
                        {{ device.activeExercises ? device.activeExercises.length : 0 }} Übungen
                      </div>

                      <div class="d-flex action-buttons">
                        <v-btn
                          v-if="userService.hasRight('ex_edit')"
                          color="blue"
                          outlined
                          small
                          class="mr-2"
                          style="min-width: 32px; padding: 0"
                          @click.stop="openDeviceDialog(device)">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="userService.hasRight('ex_delete')"
                          color="red"
                          outlined
                          small
                          style="min-width: 32px; padding: 0"
                          @click.stop="deleteDevice(device, 0)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <v-dialog v-model="deviceDialog" fullscreen hide-overlay transition="dialog-bottom-transition" dark>
      <DeviceDashboardForm v-if="deviceDialog" :deviceId="selectedDeviceId" @close="closeDeviceDialog" @saved="fetchDevices" />
    </v-dialog>

    <v-dialog v-model="deleteDialog" persistent max-width="400" dark>
      <v-card color="#1a1a1a" style="border-radius: 8px">
        <v-card-title class="text-h6 font-weight-bold white--text pt-5">Gerät löschen?</v-card-title>
        <v-card-text class="pt-4">
          <div class="text-subtitle-1 white--text font-weight-bold mb-2">{{ deletingDevice.title }}</div>
          Möchtest Du dieses Gerät und seine <strong>{{ deletingDevice.exercises ? deletingDevice.exercises.length : 0 }} Übungen</strong> wirklich
          unwiderruflich löschen?
          <div class="mt-4 pa-3" style="background-color: rgba(244, 67, 54, 0.1); border-left: 4px solid #f44336; border-radius: 4px">
            <span class="red--text font-weight-bold">Achtung:</span> Das Gerät und die Übungen werden aus allen Trainingsplänen entfernt!
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn text color="grey" small @click="deleteDialog = false">Abbrechen</v-btn>
          <v-btn outlined color="red" small class="px-4 font-weight-bold" @click="deleteDevice(deletingDevice, 1)">Endgültig löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"
import { mapState, mapActions } from "vuex"
import DeviceDashboardForm from "./devicesDashboardForm.vue"

export default {
  name: "deviceList",
  components: {
    DeviceDashboardForm,
  },
  data() {
    return {
      search: "",
      userService,
      activePanels: [], // Startet leer -> Alle Panels sind ZU! = Rasanter Lade-Boost

      // Dialog States
      deviceDialog: false,
      selectedDeviceId: null,

      // Delete States
      deleteDialog: false,
      deletingDevice: {
        title: "",
        exercises: [],
      },
    }
  },
  created() {
    this.fetchDevices()
  },
  computed: {
    ...mapState("deviceStore", ["deviceList"]),

    filteredDevices() {
      const searchTerm = (this.search || "").toLowerCase().trim()
      return this.deviceList.filter((device) => {
        if (device.deleted) return false

        device.activeExercises = device.exercises ? device.exercises.filter((e) => e && !e.deleted) : []

        if (!searchTerm) return true

        const titleMatch = device.title && device.title.toLowerCase().includes(searchTerm)
        const shortMatch = device.shortForm && device.shortForm.toLowerCase().includes(searchTerm)
        const exerciseMatch = device.activeExercises.some((ex) => ex.title && ex.title.toLowerCase().includes(searchTerm))

        return titleMatch || shortMatch || exerciseMatch
      })
    },

    groupedCategories() {
      const groups = {}
      this.filteredDevices.forEach((device) => {
        const cat = device.category || "Ohne Kategorie"
        if (!groups[cat]) groups[cat] = []
        groups[cat].push(device)
      })
      return Object.keys(groups)
        .sort()
        .map((categoryName) => ({
          name: categoryName,
          devices: groups[categoryName],
        }))
    },
  },
  watch: {
    // Wenn gesucht wird, klappen wir automatisch die Panels auf
    search(newVal) {
      if (newVal && newVal.length > 1) {
        this.activePanels = this.groupedCategories.map((_, i) => i)
      } else {
        this.activePanels = []
      }
    },
  },
  methods: {
    ...mapActions("deviceStore", ["setDeviceList", "removeDevice", "resetDevice"]),

    async fetchDevices() {
      api.fetchDevices(null).then((deviceList) => {
        this.setDeviceList(JSON.parse(JSON.stringify(deviceList)))
        this.$emit("finishedloading")
      })
    },

    openDeviceDialog(device) {
      this.resetDevice()
      this.selectedDeviceId = device ? device._id : null
      this.deviceDialog = true
    },

    closeDeviceDialog() {
      this.deviceDialog = false
      this.selectedDeviceId = null
    },

    async deleteDevice(device, status) {
      if (status === 0) {
        this.deletingDevice = device
        this.deleteDialog = true
      } else {
        // Hier übergeben wir wieder das komplette 'device' Objekt,
        // damit deine api.js die _id selbst auslesen kann!
        await api.deleteDevice(device)
        this.removeDevice(device)
        this.deleteDialog = false
      }
    },
  },
}
</script>

<style scoped>
.deviceListWrapper {
  overflow-y: auto;
  overflow-x: hidden;
}

.tracking-wide {
  letter-spacing: 1px;
}

/* Modernes Input-Styling für die Suche */
.modern-input ::v-deep .v-input__slot {
  border-color: #333 !important;
}
.modern-input ::v-deep .v-input--is-focused .v-input__slot {
  border-color: #00e676 !important;
}

/* Elegante Card Styles */
.device-card {
  transition: all 0.2s ease-in-out;
}

.device-card:hover {
  transform: translateY(-2px);
  background-color: #222 !important;
  border-color: #444 !important;
}

.image-wrapper {
  width: 100px;
  min-width: 100px;
  background-color: #121212;
  border-right: 1px solid #2a2a2a;
}

/* Text abschneiden (max 2 Zeilen) */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Vuetify Panel Anpassungen */
.transparent-panels ::v-deep .v-expansion-panel::before {
  box-shadow: none !important;
}
</style>
